import { ObjectModel } from "../../../common/types";
import { AuthProvider, getProfile, getMeasurements, getModel, getStarParams, generateModel, setTarget, deleteMeasurement, deleteClient, onShare, getLink, patchMeasurement, addResource, getResource, updateProfile } from "../../utils/http-service";
import { ClientData, MeasureData, ObjectModelData, ShareLinkData, StarParams } from "../../utils/interfaces";
import { getMeasurementFromModel } from "../../../components/measure/measureCache";
import { calculateBodyFat } from "./calculate-bf";
export interface MeasurementPoint {
    id: string;
    date: Date;
    value: number;
};

export interface DashBoardComponentProps {
    clientHolder: ClientDataHolder;
    clientData: ClientData;
    measurementDatas: MeasureData[];
    objectModels: ObjectModel[];
    shared?: ShareLinkData;
}
export class ClientDataHolder {

    ID: string;
    auth: AuthProvider;
    data?: ClientData;
    loadedMeasurements: boolean;
    loadedModels: boolean;
    clientLoaded: boolean;
    measurements: MeasureData[];
    objectModels: ObjectModelData[];
    params?: StarParams;
    onMeasurementsLoaded: (measurements: MeasureData[]) => void;
    onModelsLoaded: (models: ObjectModel[]) => void;
    setClientData: (data: ClientData) => void;
    setShared: (shared: ShareLinkData) => void;
    constructor(ID: string, auth: AuthProvider);
    constructor(
        id: string,
        auth: AuthProvider,
        setClientData: (data: ClientData) => void,
        onMeasurementsLoaded: (measurements: MeasureData[]) => void,
        onModelsLoaded: (models: ObjectModel[]) => void,
        setShared: (shared: ShareLinkData) => void
    );

    // --- Single implementation ---
    constructor(
        ID: string,
        auth: AuthProvider,
        setClientData?: (data: ClientData) => void,
        onMeasurementsLoaded?: (measurements: MeasureData[]) => void,
        onModelsLoaded?: (models: ObjectModel[]) => void,
        setShared?: (shared: ShareLinkData) => void
    ) {
        this.ID = ID;
        this.auth = auth;
        this.loadedMeasurements = false;
        this.loadedModels = false;
        this.clientLoaded = false;
        this.measurements = [];
        this.objectModels = [];

        // Handle optional callbacks
        if (setClientData && onMeasurementsLoaded && onModelsLoaded && setShared) {
            this.setClientData = setClientData;
            this.setShared = setShared;
            this.onMeasurementsLoaded = onMeasurementsLoaded;
            this.onModelsLoaded = (m) => {
                console.log("onModelsLoaded called", m.length, m[0]?.id, m[0]?.createdAt);
                onModelsLoaded(m);
            };
        } else {
            // Default no-op callbacks
            this.setClientData = () => { };
            this.setShared = () => { };
            this.onMeasurementsLoaded = () => { };
            this.onModelsLoaded = () => { };
        }
    }
    updateClient = async (updatedData: Partial<ClientData>) => {
        if (!this.data) {
            throw new Error('Client data not loaded');
        }
        console.log('Updating client', this.ID, updatedData);
        const newData = await updateProfile(this.ID, updatedData, this.auth);
        console.log('Client updated:', newData);
        this.data = newData;
        this.setClientData(newData);
        return newData;
    }
    getShareLink = async (): Promise<ShareLinkData> => {
        if (!this.data) {
            throw new Error('Client data not loaded');
        }

        const shareLink = await getLink(this.ID, this.auth);
        if (shareLink) {
            this.setShared(shareLink);
            console.log('Share link created:', shareLink);
        } else {
            console.warn('Failed to create share link');
        }
        return shareLink;
    }
    onShare = async (enabled: boolean) => {
        console.log('onShare called', this.ID, enabled);
        if (!this.data) {
            throw new Error('Client data not loaded');
        }
        const shared = await onShare(enabled, this.ID, this.auth)
        if (shared) {
            this.setShared(shared);
            console.log('Share link created:', shared);
        } else {
            console.warn('Failed to create share link');
        }
        return shared;
    }

    deleteMeasurements = async (measurementIds: string[]) => {
        if (!this.data) {
            throw new Error('Client data not loaded');
        }

        for (const measurementId of measurementIds) {
            const sessionID = this.measurements.find(m => m.ID === measurementId)?.sessionID;
            if (!sessionID) {
                console.warn('Measurement not found for ID:', measurementId);
                continue;
            }
            console.log('Deleting measurement with ID:', measurementId, 'for sessionID:', sessionID);

            const res = await deleteMeasurement(this.ID, sessionID, this.auth);
            if (res.ok) {
                this.measurements = this.measurements.filter(m => m.ID !== measurementId);
                this.objectModels = this.objectModels.filter(m => m.sessionID !== sessionID);
            }
        }
        this.onMeasurementsLoaded(this.measurements);
        this.onModelsLoaded(toModels(this.objectModels, this.measurements));
    }
    deleteClient = async () => {
        console.log('Deleting client with ID:', this.ID);
        if (!this.data) {
            throw new Error('Client data not loaded');
        }
        const res = await deleteClient(this.ID, this.auth);
        return res.ok;
    }



    getStarParamsLatest = async () => {
        if (!this.params) {
            await this.loadStarParams();
        }
        return this.loadStarParams();
    }
    loadStarParams = async () => {
        if (!this.data) {
            await this.loadClientData();
        }
        if (!this.measurements) {
            await this.loadMeasurements();
        }
        const sessionID = this.measurements[this.measurements.length - 1].sessionID;

        const params = await getStarParams(this.ID, sessionID, this.auth)
        this.params = params;
        return params;
    }
    getEstimation = async (percentage: number) => {
        const meas = await this.getMeasurements();
        const sessionID = meas[this.measurements.length - 1].sessionID;
        const result = await generateModel(this.ID, sessionID
            , percentage, this.auth);
        return result;
    }

    getClient = async (force?: boolean): Promise<ClientData> => {
        if (force) {
            return this.loadClientData();
        }

        const data = this.data || this.loadClientData();
        this.setClientData(await data)
        return data
    }
    getMeasurements = async (unit: 'cm' | 'mm' = 'cm'): Promise<MeasureData[]> => {
        const clientData = await this.getClient();

        let measurements = this.loadedMeasurements ? this.measurements : await this.loadMeasurements();
        if (unit === 'mm') {
            measurements = measurements.map(m => {
                const measure = m.measurements;
                return {
                    ...m,
                    measurements: {
                        ...measure,
                        head: measure.head * 10,
                        neck: measure.neck * 10,
                        chest: measure.chest * 10,
                        waist: measure.waist * 10,
                        buttocks: measure.buttocks * 10,
                        leg_inner: measure.leg_inner * 10,
                        arm_outer: measure.arm_outer * 10,
                        height: measure.height * 10,
                        leg_circ: measure.leg_circ ? measure.leg_circ * 10 : undefined,
                        arm_circ: measure.arm_circ ? measure.arm_circ * 10 : undefined,
                        waist_female: (measure.waist_female || 0) * 10
                    }
                }
            });
        }

        return measurements;
    }
    getModels = async (): Promise<ObjectModel[]> => {
        const models = await this.getObjectModels();
        const measurements = await this.getMeasurements('mm');
        return toModels(models, measurements);
    }
    getObjectModels = async (): Promise<ObjectModelData[]> => {
        return this.loadedModels ? this.objectModels : this.loadModels();
    }
    setTarget = async (percentage: number): Promise<ClientData | undefined> => {
        if (!this.data) {
            throw new Error('Client data not loaded');
        }

        console.log('setTarget', this.ID, percentage);
        const sessionID = this.measurements[this.measurements.length - 1].sessionID;
        const targetMeasure: MeasureData['measurements'] = { body_fat: percentage, head: 0, neck: 0, chest: 0, waist: 0, buttocks: 0, leg_inner: 0, arm_outer: 0, height: 0, waist_female: 0, leg_circ: 0, arm_circ: 0, weight: 0 };
        let res = await setTarget(this.ID, sessionID, targetMeasure, this.auth);
        if (res.ok) {
            const target: MeasureData = await res.json();
            if (this.data) {
                this.data = { ...this.data, target: target };
            }
            this.setClientData(this.data);
            return this.data;
        }
    }
   addResourceToSession = async (sessionID: string, resourceName: string, data: any, raw = false): Promise<any> => {
        if (!this.ID) {
            throw new Error('Profile ID not set');
        }
        return addResource(this.ID, sessionID, resourceName, data, this.auth, raw);
    }
    getResourceFromSession = async ( sessionID: string,resourceName: string, raw = false): Promise<any> => {
        if (!this.ID) {
            throw new Error('Profile ID not set');
        }
        if (!raw){
            const result = await getResource(this.ID, sessionID, resourceName, this.auth, raw);
            console.log('Fetched resource', resourceName, 'for session', sessionID, 'data:', result);
            return JSON.parse(result.data);
        }
        return await getResource(this.ID, sessionID, resourceName, this.auth, raw);
    }

    loadModels = async () => {
        const unit = 'cm'
        const measurements = await this.getMeasurements(unit);

        let localModelsP: Promise<{ model: ObjectModelData; updatedMeasurement: MeasureData }>[] = [];
        if (this.data?.gender === 'female') {
            localModelsP = measurements.map(async (m) => {
                const d = await getModel(this.ID, m.sessionID, this.auth);
                const res = await getMeasurementFromModel({ obj: String(d), sessionID: m.sessionID, inputHeightCm: m.measurements.height });
                const scale = res.scale || m.scale || 1;
                console.log('Applying scale factor to female waist:', scale, m.scale);
                const waist_female = res.best.slice.hullLength * (scale || 1);
                const updatedMeasurements = { ...m.measurements, waist_female };
                const body_fat = Math.floor(calculateBodyFat(updatedMeasurements, this.data?.gender ?? 'female', unit));
                const updatedMeasurement: MeasureData = {
                    ...m,
                    measurements: { ...updatedMeasurements, body_fat }
                };
                return {
                    model: { obj: d, createdAt: m.createdAt, sessionID: m.sessionID, profileID: this.ID, ID: m.ID } as ObjectModelData,
                    updatedMeasurement
                };
            });
        } else if (this.data?.gender === 'male') {
            localModelsP = measurements.map(async (m) => {
                const d = await getModel(this.ID, m.sessionID, this.auth);
                const body_fat = calculateBodyFat(m.measurements, 'male', unit);
                const updatedMeasurement: MeasureData = {
                    ...m,
                    measurements: { ...m.measurements, body_fat }
                };
                return {
                    model: { obj: d, createdAt: m.createdAt, sessionID: m.sessionID, profileID: this.ID, ID: m.ID } as ObjectModelData,
                    updatedMeasurement
                };
            });
        }

        const results = await Promise.all(localModelsP);
        const localModels = results.map(r => r.model);
        const updatedMeasurements = results.map(r => r.updatedMeasurement);

        this.loadedModels = true;
        this.objectModels = localModels;
        this.measurements = updatedMeasurements;
        this.loadedMeasurements = true;
        this.onModelsLoaded(toModels(localModels, updatedMeasurements));
        this.onMeasurementsLoaded([...updatedMeasurements]);
        return this.objectModels;
    }
    onConfirmMeasurement = async (measurement: MeasureData): Promise<void> => {
        // replace measurement in this.measurements
        if (!this.data) {
            alert("Client data not loaded");
            return;
        }
        const index = this.measurements.findIndex(m => m.ID === measurement.ID);
        if (index !== -1) {
            const updatedMeasurement = await patchMeasurement(this.ID, measurement.ID, measurement, this.auth);
            const toInsert = normalizeToHeight(updatedMeasurement, this.data.height);
            this.measurements[index] = toInsert;
            console.log('Measurement updated:', toInsert);
        } else {
            console.warn("Measurement not found for ID:", measurement.ID);
        }
        this.onMeasurementsLoaded(this.measurements);
    }

    loadMeasurements = async (): Promise<MeasureData[]> => {
        this.measurements = await getMeasurements(this.ID, this.auth);
        const d = this.data || await getProfile(this.ID, this.auth);
        this.measurements = this.measurements.map(m => normalizeToHeight(m, d.height));
        return this.measurements;
    }
    loadClientData = async (): Promise<ClientData> => {
        this.data = await getProfile(this.ID, this.auth);
        this.clientLoaded = true;
        return this.data;
    }
    load = async () => {
        await this.loadClientData();
        await this.loadMeasurements();
        await this.loadModels();
        await this.getShareLink();
    }
    isLoaded = () => {
        return this.loadedModels && this.loadedMeasurements && this.clientLoaded;
    }
}

const getValue = (data: MeasureData, measurementType: string) => {
    const m = data.measurements;
    switch (measurementType) {
        case 'neck':
            return m.neck;
        case 'weight':
            return m.weight;
        case 'waist':
            return m.waist;
        case 'buttocks':
            return m.buttocks;
        case 'height':
            return m.height;
        case 'chest':
            return m.chest;
        default:
            return 0;
    }
}


const normalizeToHeight = (measureData: MeasureData, heightInput?: number): MeasureData => {
    const measure = { ...measureData.measurements };
    let scale = 1;
    if (!heightInput || heightInput === 0) {
        scale = 1;
    }else{
        scale = (heightInput * 10) / measure.height;
    }
    measureData.scale = scale;
    measure.head = measure.head / 10 * scale;
    measure.neck = measure.neck / 10 * scale;
    measure.chest = measure.chest / 10 * scale;
    measure.waist = measure.waist / 10 * scale;
    measure.buttocks = measure.buttocks / 10 * scale;
    measure.leg_inner = measure.leg_inner / 10 * scale;
    measure.arm_outer = measure.arm_outer / 10 * scale;
    measure.height = measure.height / 10 * scale;
    measure.leg_circ = measure.leg_circ ? measure.leg_circ / 10 * scale : undefined;
    measure.arm_circ = measure.arm_circ ? measure.arm_circ / 10 * scale : undefined;
    if (measure.waist_female !== undefined) {
        measure.waist_female = measure.waist_female / 10 * scale;
    }
    return { ...measureData, measurements: measure };
}
export const toObjectModel = (model: ObjectModelData, measurements: MeasureData): ObjectModel => {
    return {
        id: model.ID,
        createdAt: model.createdAt + '',
        obj: model.obj,
        visibleEffects: [0],
        showMeasurement: '',
        measurements: measurements.measurements
    }
}


function toModels(models: ObjectModelData[], measurements: MeasureData[]): ObjectModel[] {
    return models
        .filter(m => m.sessionID && measurements.some(meas => meas.sessionID === m.sessionID))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((m) => toObjectModel(m, measurements.find(meas => meas.sessionID === m.sessionID) as MeasureData));
}
